<template>
  <div class="location-selector relative">
    <!-- Location combobox (visible when logged in with 1 or more locations) - Desktop -->
    <div v-if="isAuthenticated && locations.length >= 1" class="relative location-dropdown hidden md:block" ref="locationDropdownRef">
      <button
        @click="toggleLocationDropdown"
        :class="[
          'text-[15px] leading-7 text-white font-archivo font-medium flex items-center gap-1',
          locations.length <= 1 ? 'cursor-default' : 'cursor-pointer'
        ]"
        :disabled="isLoading"
        style="max-width: 250px;"
      >
        <span
          v-if="selectedLocation"
          class="truncate max-w-[210px]"
          :title="selectedLocation.description"
        >
          {{ selectedLocation.description.length > 20 ? selectedLocation.description.substring(0, 20) + '...' : selectedLocation.description }}
        </span>
        <span v-else>{{ $t('location.selectLocation') }}</span>
        <svg
          v-if="locations.length > 1"
          class="w-4 h-4 flex-shrink-0"
          :class="{ 'transform rotate-180': isLocationDropdownOpen }"
          viewBox="0 0 24 24"
          fill="#FFDD00"
        >
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
        </svg>
      </button>

      <!-- Dropdown Desktop -->
      <div v-if="isLocationDropdownOpen" class="absolute top-full left-0 z-50">
        <div class="menu-level bg-black py-1 max-h-[500px] overflow-y-auto border-0 border-none" style="width: 250px; background-color: #000000;">
          <!-- Loading State -->
          <div v-if="isLoading" class="px-4 py-2 text-empire-yellow">
            {{ $t('common.loading') }}...
          </div>

          <!-- Locations List -->
          <template v-else>
            <div
              v-for="location in locations"
              :key="location.id"
              class="location-item"
            >
              <div
                :class="[
                  'flex items-center justify-between px-4 py-2 cursor-pointer transition-colors duration-200 group font-medium tracking-wide whitespace-nowrap overflow-hidden truncate',
                  selectedLocation && selectedLocation.id === location.id ? 'bg-empire-yellow text-black' : 'text-white hover:bg-empire-yellow hover:text-black'
                ]"
                :title="location.description"
                @click="selectLocation(location)"
              >
                <!-- Location Description -->
                {{ location.description.length > 20 ? location.description.substring(0, 20) + '...' : location.description }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Mobile Version -->
    <div v-if="isAuthenticated && locations.length >= 1" class="relative location-dropdown md:hidden" ref="locationDropdownRefMobile">
      <button
        @click="toggleLocationDropdown"
        :class="[
          'px-1 py-1 text-[13px] leading-6 text-white font-archivo font-medium text-center flex items-center justify-center gap-1',
          locations.length <= 1 ? 'cursor-default' : 'cursor-pointer'
        ]"
        :disabled="isLoading"
        style="max-width: 120px; min-width: 80px;"
      >
        <span v-if="selectedLocation" class="truncate max-w-[80px]" :title="selectedLocation.description">
          {{ selectedLocation.description.length > 10 ? selectedLocation.description.substring(0, 10) + '...' : selectedLocation.description }}
        </span>
        <span v-else class="truncate max-w-[80px]">{{ $t('location.selectLocation') }}</span>
        <svg
          v-if="locations.length > 1"
          class="w-4 h-4 flex-shrink-0"
          :class="{ 'transform rotate-180': isLocationDropdownOpen }"
          viewBox="0 0 24 24"
          fill="#FFDD00"
        >
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
        </svg>
      </button>

      <!-- Dropdown Mobile -->
      <div
        v-if="isLocationDropdownOpen"
        class="fixed z-50 bg-black shadow-lg rounded-lg mt-2 mobile-location-menu"
        :style="dropdownMobileStyle"
      >
        <div class="max-h-[40vh] overflow-y-auto py-1" style="width: 180px;">
          <!-- Loading State -->
          <div v-if="isLoading" class="px-4 py-2 text-empire-yellow text-xs">
            {{ $t('common.loading') }}...
          </div>

          <!-- Locations List -->
          <template v-else>
            <div
              v-for="location in locations"
              :key="location.id"
              class="location-item"
            >
              <div
                :class="[
                  'flex items-center justify-between px-3 py-2 cursor-pointer transition-colors duration-200 group font-medium tracking-wide whitespace-nowrap overflow-hidden truncate text-xs',
                  selectedLocation && selectedLocation.id === location.id ? 'bg-empire-yellow text-black' : 'text-white hover:bg-empire-yellow hover:text-black'
                ]"
                :title="location.description"
                @click="selectLocation(location)"
              >
                <!-- Location Description -->
                {{ location.description.length > 15 ? location.description.substring(0, 15) + '...' : location.description }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Location Change Confirmation Modal -->
    <div
      v-if="showConfirmationModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] px-4"
      @click.self="cancelLocationChange"
    >
      <div class="bg-white p-6 max-w-md w-full">
        <h2 class="text-xl font-archivo-narrow font-bold mb-4">{{ $t('location.confirmChange.title') }}</h2>
        <p class="mb-4 font-archivo">{{ $t('location.confirmChange.message') }}</p>
        <p class="mb-6 font-archivo font-semibold">{{ pendingLocation?.description }}</p>
        <div class="flex gap-4">
          <button
            @click="cancelLocationChange"
            class="flex-1 bg-gray-300 text-black py-3 font-archivo-narrow font-semibold"
          >
            {{ $t('location.confirmChange.cancel') }}
          </button>
          <button
            @click="confirmLocationChange"
            class="flex-1 bg-empire-yellow text-black py-3 font-archivo-narrow font-semibold"
          >
            {{ $t('location.confirmChange.confirm') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { locationService } from '@/services/locationService'
import { useCartStore } from '@/stores/cartStore'
import eventBus from '@/utils/eventBus'

export default {
  name: 'LocationSelector',
  setup() {
    const { t } = useI18n()
    const cartStore = useCartStore()

    const selectedLocation = ref(null)
    const selectedLocationId = ref(null)
    const locations = ref([])
    const isLocationDropdownOpen = ref(false)
    const isLoading = ref(false)
    const locationDropdownRef = ref(null)
    const locationDropdownRefMobile = ref(null)
    const showConfirmationModal = ref(false)
    const pendingLocation = ref(null)

    // Mobile detection
    const isMobile = ref(window.innerWidth < 768)
    const handleResize = () => { isMobile.value = window.innerWidth < 768 }

    const isAuthenticated = computed(() => {
      const token = localStorage.getItem('token')
      return !!token
    })

    // Computed to calculate the mobile dropdown position
    const dropdownMobileStyle = computed(() => {
      if (!locationDropdownRefMobile.value || !isLocationDropdownOpen.value) {
        return {}
      }

      const rect = locationDropdownRefMobile.value.getBoundingClientRect()
      const dropdownWidth = 180
      const dropdownMaxHeight = window.innerHeight * 0.4 // 40vh
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      let left = rect.left + (rect.width / 2) - (dropdownWidth / 2)
      let top = rect.bottom + 8 // 8px de margem

      // Adjusts when it is too far to the left
      if (left < 8) {
        left = 8
      }

      // Adjusts when it is too far to the right
      if (left + dropdownWidth > viewportWidth - 8) {
        left = viewportWidth - dropdownWidth - 8
      }

      // Adjusts when there is no room below
      if (top + dropdownMaxHeight > viewportHeight - 8) {
        // Tries to place it above
        top = rect.top - dropdownMaxHeight - 8
        // If it still does not fit, adjust it to the top of the screen
        if (top < 8) {
          top = 8
        }
      }

      return {
        left: `${left}px`,
        top: `${top}px`
      }
    })

    const toggleLocationDropdown = (event) => {
      if (event) {
        event.stopPropagation()
      }

      if (locations.value.length > 1) {
        isLocationDropdownOpen.value = !isLocationDropdownOpen.value

        // If the dropdown is being opened, emit an event to close the other dropdowns
        if (isLocationDropdownOpen.value) {
          eventBus.emit('dropdown-opened', 'location')
        }
      }
    }

    // Method to close the location dropdown
    const closeDropdown = () => {
      if (isLocationDropdownOpen.value) {
        isLocationDropdownOpen.value = false
      }
    }

    const selectLocation = (location) => {
      if (selectedLocation.value && selectedLocation.value.id === location.id) {
        isLocationDropdownOpen.value = false
        return
      }

      // If there are items in the cart, show a confirmation modal
      if (cartStore.items.length > 0) {
        pendingLocation.value = location
        showConfirmationModal.value = true
        isLocationDropdownOpen.value = false
      } else {
        // If there are no items in the cart, switch directly
        applyLocationChange(location)
      }
    }

    const confirmLocationChange = () => {
      if (pendingLocation.value) {
        applyLocationChange(pendingLocation.value)
        showConfirmationModal.value = false
        pendingLocation.value = null
      }
    }

    const cancelLocationChange = () => {
      showConfirmationModal.value = false
      pendingLocation.value = null
    }

    const applyLocationChange = async (location) => {
      selectedLocation.value = location
      selectedLocationId.value = location.id

      const locationData = {
        id: location.id,
        description: location.description
      }

      locationService.saveSelectedLocation(locationData)
      console.log('Location alterada para:', locationData)

      // Recalculates the cart prices while keeping the items
      try {
        await cartStore.recalculatePrices(location.id)
        console.log('Preços do carrinho recalculados com sucesso para nova filial')
      } catch (error) {
        console.error('Erro ao recalcular preços do carrinho:', error)
        // On error, keep the items but show a warning
        // Does not clear the cart so the user does not lose their items
      }

      // Emit the location change event
      eventBus.emit('location-changed', locationData)

      isLocationDropdownOpen.value = false
    }

    const loadLocations = async () => {
      isLoading.value = true
      try {
        const allLocations = await locationService.getLocations()
        locations.value = allLocations

        const savedLocation = locationService.getSelectedLocation()
        if (savedLocation && savedLocation.id) {
          const locationExists = allLocations.find(l => l.id === savedLocation.id)
          if (locationExists) {
            selectedLocation.value = locationExists
            selectedLocationId.value = locationExists.id
          } else if (allLocations.length > 0) {
            selectedLocation.value = allLocations[0]
            selectedLocationId.value = allLocations[0].id
            locationService.saveSelectedLocation(allLocations[0])
          }
        } else if (allLocations.length > 0) {
          selectedLocation.value = allLocations[0]
          selectedLocationId.value = allLocations[0].id
          locationService.saveSelectedLocation(allLocations[0])
        }
      } catch (error) {
        console.error('Erro ao carregar locations:', error)
      } finally {
        isLoading.value = false
      }
    }

    const handleClickOutside = (event) => {
      if (locationDropdownRef.value && !locationDropdownRef.value.contains(event.target) &&
          locationDropdownRefMobile.value && !locationDropdownRefMobile.value.contains(event.target)) {
        isLocationDropdownOpen.value = false
      }
    }

    onMounted(() => {
      if (isAuthenticated.value) {
        loadLocations()
      }
      document.addEventListener('click', handleClickOutside)
      window.addEventListener('resize', handleResize)

      eventBus.on('selected-location-changed', (location) => {
        console.log('Evento selected-location-changed recebido:', location)
        selectedLocation.value = location
        selectedLocationId.value = location.id
      })

      // Adds a listener for the dropdown-opened event
      eventBus.on('dropdown-opened', (source) => {
        // Applies the mutual exclusion on mobile only
        if (source !== 'location') {
          console.log('Outro dropdown foi aberto (mobile), fechando dropdown de locations')
          isLocationDropdownOpen.value = false
        }
      })
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
      window.removeEventListener('resize', handleResize)
      eventBus.off('selected-location-changed')
      eventBus.off('dropdown-opened')
    })

    watch(isAuthenticated, (newValue) => {
      if (newValue) {
        loadLocations()
      } else {
        locations.value = []
        selectedLocation.value = null
        selectedLocationId.value = null
      }
    })

    return {
      selectedLocation,
      selectedLocationId,
      locations,
      isLocationDropdownOpen,
      isLoading,
      locationDropdownRef,
      locationDropdownRefMobile,
      showConfirmationModal,
      pendingLocation,
      isAuthenticated,
      toggleLocationDropdown,
      selectLocation,
      confirmLocationChange,
      cancelLocationChange,
      closeDropdown,
      dropdownMobileStyle,
      t
    }
  }
}
</script>

<style scoped>
.location-dropdown {
  position: relative;
}

.menu-level {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}
</style>

